import React from "react";
import TextareaAutosize from "react-textarea-autosize";
import styled from "styled-components";
import { LifeAtBodySecondary } from "@common/LifeAtText/LifeAtText";
import { FontSize } from "@themes/Fonts";
import { ScrollbarStyles } from "@themes/ScrollbarStyles";
import Styles from "@themes/Styles";

/* ------------------------------------------------------------- */
/* ---------------- TextInputComponents.TextArea --------------- */
/* ------------------------------------------------------------- */
const TextArea = styled(TextareaAutosize)<{ $minHeight?: number; $maxHeight?: number; fontSizeCss?: string }>`
    width: 100%;
    min-height: ${(props) => props.$minHeight ?? `50`}px;
    max-height: ${(props) => props.$maxHeight ?? `70`}px;
    height: fit-content;
    padding: 5px;
    overflow-y: auto;
    border: none;
    resize: none;
    background-color: inherit;
    color: ${({ theme }) => theme.textColorPrimary};
    &:disabled {
        color: ${({ theme }) => theme.textColorSecondary};
    }
    font-size: ${(props) => (props.fontSizeCss ? props.fontSizeCss : FontSize.normal)};
    font-family: inherit;
    cursor: inherit;

    &:focus,
    &:focus-visible {
        outline: none;
        font-size: ${(props) => (props.fontSizeCss ? props.fontSizeCss : FontSize.normal)};
    }

    &::placeholder {
        font-style: italic;
    }

    ${ScrollbarStyles};
`;

/* ------------------------------------------------------------- */
/* ------------ TextInputComponents.TextAreaWrapper ------------ */
/* ------------------------------------------------------------- */

const TextDisplayWrapper = styled.div<{ showBorder: boolean; error: boolean; disabled: boolean }>`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: baseline;
    min-width: fit-content;
    width: 100%;
    background-color: ${(props) => (props.disabled ? props.theme.backgroundSpecial : props.theme.backgroundPrimary)};

    ${(props) => props.disabled && `cursor: not-allowed;`}

    padding: 4px;
    border-radius: ${Styles.borderRadiusSharp};
    border: ${(props) => (props.showBorder ? `1px solid ${props.theme.borderPrimary}` : `none`)};
    ${(props) => props.error && props.showBorder && `border: 1px solid ${props.theme.textColorSpecial}`}
`;

const InputLabel = styled.label`
    margin-bottom: 4px;
`;

const InputWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: inherit;
`;

const ErrorMessage = styled.span`
    color: ${(props) => props.theme.textColorSpecial};
    font-size: ${FontSize.small};
`;

interface TextInputFieldWrapperProps {
    readonly children: React.ReactNode;
    readonly label?: string;
    readonly showBorder?: boolean;
    readonly errorMessage?: string;
    readonly isDisabled?: boolean;
    readonly className?: string;
}
export function TextInputFieldWrapper(props: TextInputFieldWrapperProps) {
    return (
        <InputWrapper className={props.className}>
            {props.label && (
                <InputLabel>
                    <LifeAtBodySecondary>{props.label}</LifeAtBodySecondary>
                </InputLabel>
            )}
            <TextDisplayWrapper
                showBorder={!!props.showBorder}
                error={!!props.errorMessage}
                disabled={!!props.isDisabled}
            >
                {props.children}
            </TextDisplayWrapper>
            {props.errorMessage && <ErrorMessage>{props.errorMessage}</ErrorMessage>}
        </InputWrapper>
    );
}

export const TextInputComponents = {
    TextArea: TextArea,
    TextAreaWrapper: TextInputFieldWrapper,
};
